import clsx from 'clsx'
import { memo, ReactNode } from 'react'

type Values = string | number

interface ButtonGroupProps<T extends Values> {
  values: readonly T[]
  activeValue: T
  className?: string
  activeButtonClassName?: string
  transitionSliderActiveClassName?: Record<T, string>
  disabled?: boolean
  onChange: (x: T) => void
  unit?: string
  names?: readonly string[]
  icons?: ReactNode[]
  afterIcons?: ReactNode[]
  large?: boolean
  roundedCorners?: boolean
}

const MAX_BUTTON_GROUP_SIZE_WITH_ICONS = 2

const ButtonGroupPlain = <T extends Values>({
  values,
  activeValue,
  className = '',
  activeButtonClassName = 'text-th-fgd-1',
  transitionSliderActiveClassName,
  disabled,
  onChange,
  unit,
  names,
  icons,
  afterIcons,
  large,
  roundedCorners = true,
}: ButtonGroupProps<T>) => {
  function makeButtonText(value: T, index: number) {
    return names
      ? unit
        ? names[index] + unit
        : names[index]
      : unit
        ? value + unit
        : value
  }

  return (
    <div
      className={clsx(
        'w-full bg-th-bkg-2',
        disabled && 'opacity-50',
        roundedCorners && 'rounded-full',
      )}
    >
      <div className="relative flex">
        {activeValue && values.includes(activeValue) ? (
          <div
            className={clsx(
              'default-transition absolute left-0 top-0 h-full',
              transitionSliderActiveClassName?.[activeValue] ??
                'border-2 border-th-active',
              roundedCorners && 'rounded-full',
            )}
            style={{
              transform: `translateX(${
                values.findIndex((v) => v === activeValue) * 100
              }%)`,
              width: `${100 / values.length}%`,
            }}
          />
        ) : null}
        {values.map((v, i) => (
          <button
            className={clsx(
              className,
              'default-transition relative w-1/2 cursor-pointer px-3 text-center disabled:cursor-not-allowed',
              large ? 'min-h-9 text-sm' : 'min-h-7 text-xs',
              (afterIcons?.[i] != null ||
                icons?.length === MAX_BUTTON_GROUP_SIZE_WITH_ICONS) &&
                'flex items-center justify-center',
              'font-semibold',
              v === activeValue
                ? activeButtonClassName
                : 'text-th-fgd-4 md:hover:text-th-fgd-1',
            )}
            disabled={disabled}
            key={`${v}${i}`}
            onClick={() => onChange(v)}
            style={{ width: `${100 / values.length}%` }}
            type="button"
          >
            {icons?.length === MAX_BUTTON_GROUP_SIZE_WITH_ICONS
              ? icons[i]
              : null}
            {afterIcons?.[i] ? (
              <>
                <div className="flex-1">{makeButtonText(v, i)}</div>
                {afterIcons[i]}
              </>
            ) : (
              makeButtonText(v, i)
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

const ButtonGroup = memo(ButtonGroupPlain) as typeof ButtonGroupPlain

export default ButtonGroup
